/**
 * @file IntegrationsPage.tsx
 * @brief Settings → Integrations (W29, Z1): Zapier and Asana in one place.
 *        Web and the Mac app (/app/integrations, /company/settings/integrations).
 *
 * @details
 * Zapier: Connect opens the VERBLIZR app page on Zapier (the link comes from
 * Super Admin → Links, `ZAPIER_APP_URL`; no link saved = no button, never a
 * guessed address). The user then picks VERBLIZR inside Zapier and approves on
 * our consent screen. Active Zapier connections are listed with Disconnect,
 * which ends the token and its hooks at once (DELETE /api/mcp/connections/:id).
 *
 * Company gate: the `zapier` company switch binds admins too and is opt-in
 * whatever "Only share inside the company" says. A company admin gets the same
 * switch as Company → Features here; a member sees "ask your company admin".
 *
 * @references
 * - scratch/claude-code-report.md "## Z1" (contract), ADR-060
 * - features/company-features/CompanyFeaturesPage.tsx (same switch)
 * - features/mcp-connect/ConnectedAiApps.tsx (the AI apps, on Profile)
 *
 * W31 (27-10-2026): cards instead of plain rows (mockup
 * scratch/dispatcher/w31-integrations-mockup.html): a full-width Zapier card
 * with its own wordmark (M1: half width, next to Make), an Asana card (text only, Asana's brand rules) and an
 * AI assistants card pointing at Profile → Connected apps. Same data and switch
 * logic as W29; only the layout changed. Phone width stacks the cards.
 *
 * M1 (29-10-2026): Make gets the same card next to Zapier, with its own
 * logo, company switch (`make`), Connect link (`MAKE_APP_URL`) and
 * connections (`kind: "make"`). Both cards come from one `AutomationCard`.
 * Zapier and Make sit side by side; Asana and AI assistants below.
 *
 * W32 (30-10-2026): when Super Admin pauses an app for the whole platform
 * (B42, public config `integrations`), its card says "Not available right
 * now" with no Connect button and no ideas. Existing connections stay listed
 * (marked "Paused by VERBLIZR") so they can still be disconnected.
 *
 * @author DES (prepared with Claude)
 * @date 27-10-2026
 */

import { useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, ExternalLink, ListTodo, LoaderCircle, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import { GridBand, GridBlock, GridCell } from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { useCompanyFeatures, useUpdateCompanyFeatures } from "@/features/company-features/api";
import { useConnectorStatus } from "@/features/connectors/api";
import { useAiConnections, useRevokeAiConnection } from "@/features/mcp-connect/api";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { toast } from "@/shared/hooks/useToast";
import { integrationLive, safeHttps } from "@/shared/publicConfig/publicConfig";
import { usePublicConfig } from "@/shared/publicConfig/usePublicConfig";
import { formatRelative } from "@/shared/utils/formatters";
import {
  MAKE_ASK_ADMIN_TEXT,
  MAKE_CONNECT_TEXT,
  MAKE_IDEAS,
  MAKE_SWITCH_TEXT,
  ZAPIER_ASK_ADMIN_TEXT,
  ZAPIER_CONNECT_TEXT,
  ZAPIER_IDEAS,
  ZAPIER_SWITCH_TEXT,
  isAutomationKind,
  type AutomationKind,
} from "./zapierCopy";
import { MakeLogo } from "./MakeLogo";
import { ZapierWordmark } from "./ZapierWordmark";

/** One automation app's words, logo and keys (Zapier Z1/W29, Make M1). */
type AutomationApp = {
  /** Connection `kind`, company feature key and entitlement key. */
  key: AutomationKind;
  name: string;
  logo: ReactNode;
  connectText: string;
  switchText: string;
  askAdminText: string;
  ideas: [string, string][];
  ideasLabel: string;
  /** Public config key of the Connect link (Super Admin → Links). */
  urlKey: "ZAPIER_APP_URL" | "MAKE_APP_URL";
  howText: string;
  disconnectedText: string;
};

const ZAPIER: AutomationApp = {
  key: "zapier",
  name: "Zapier",
  logo: <ZapierWordmark height={24} />,
  connectText: ZAPIER_CONNECT_TEXT,
  switchText: ZAPIER_SWITCH_TEXT,
  askAdminText: ZAPIER_ASK_ADMIN_TEXT,
  ideas: ZAPIER_IDEAS,
  ideasLabel: "Ideas for Zaps",
  urlKey: "ZAPIER_APP_URL",
  howText: "Open VERBLIZR on Zapier, make a Zap and sign in when Zapier asks. You approve it here.",
  disconnectedText: "Its Zaps can no longer read or send your data.",
};

const MAKE: AutomationApp = {
  key: "make",
  name: "Make",
  logo: <MakeLogo height={22} />,
  connectText: MAKE_CONNECT_TEXT,
  switchText: MAKE_SWITCH_TEXT,
  askAdminText: MAKE_ASK_ADMIN_TEXT,
  ideas: MAKE_IDEAS,
  ideasLabel: "Ideas for scenarios",
  urlKey: "MAKE_APP_URL",
  howText: "Open VERBLIZR on Make, add a VERBLIZR module to a scenario and sign in when Make asks. You approve it here.",
  disconnectedText: "Its scenarios can no longer read or send your data.",
};

/** One integration: logo or name, status chip, then its content. */
function IntegrationCard({ title, status, children }: { title: ReactNode; status: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-card p-4 md:p-5">
      <div className="flex min-h-7 items-center gap-3">
        {title}
        <span className="ml-auto">{status}</span>
      </div>
      {children}
    </section>
  );
}

/** Icon + plain-text name, for the cards with no partner logo. */
function CardName({ icon: Icon, name }: { icon: typeof ListTodo; name: string }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="grid h-7 w-7 place-items-center rounded-lg border border-border/70 bg-muted/50 text-brand">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <h3 className="text-base font-semibold">{name}</h3>
    </span>
  );
}

/** Company admin only: the app's switch (`zapier` / `make`), as in Company → Features. */
function AutomationCompanySwitch({ app }: { app: AutomationApp }) {
  const { refreshUser } = useAuth();
  const features = useCompanyFeatures();
  const update = useUpdateCompanyFeatures();
  const on = features.data?.featureAccess[app.key] === true;
  const locked = features.data?.platformLocks[app.key] === true;
  // An older backend has no key for this app: no switch to show.
  if (features.data && features.data.featureAccess[app.key] === undefined) return null;

  const onToggle = async (next: boolean) => {
    try {
      await update.mutateAsync({ [app.key]: next });
      // The admin's own Connect button follows the switch (entitlements).
      void refreshUser();
      toast({ variant: "success", title: next ? `${app.name} enabled` : `${app.name} disabled` });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border/70 bg-muted/40 px-3.5 py-3">
      <div>
        <div className="flex items-center gap-2 text-sm font-medium">
          Allow {app.name} for the company
          {update.isPending ? <LoaderCircle className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {locked ? "Locked off by Verblizr platform settings — not available yet." : app.switchText}
        </p>
      </div>
      <Switch
        checked={on}
        disabled={features.isLoading || update.isPending || locked}
        onCheckedChange={(next) => void onToggle(next)}
        aria-label={`${app.name} ${on ? "enabled" : "disabled"}`}
      />
    </div>
  );
}

function AutomationConnections({ app }: { app: AutomationApp }) {
  const list = useAiConnections();
  const revoke = useRevokeAiConnection();
  const rows = (list.data ?? []).filter((c) => c.kind === app.key);

  const onDisconnect = async (id: string) => {
    try {
      await revoke.mutateAsync(id);
      toast({ title: `${app.name} disconnected`, description: app.disconnectedText });
    } catch {
      toast({ title: "Could not disconnect", description: "Please try again.", variant: "destructive" });
    }
  };

  return (
    <div className="border-t border-border/60 pt-3">
      <div className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Your connections
      </div>
      {rows.length === 0 ? (
        <p className="py-1.5 text-sm text-muted-foreground">
          {list.isLoading
            ? "Checking…"
            : list.isError
              ? "Could not load your connections."
              : `No ${app.name} connections yet.`}
        </p>
      ) : (
        <ul>
          {rows.map((c) => {
            const busy = revoke.isPending && revoke.variables === c.id;
            return (
              <li
                key={c.id}
                className="flex items-center gap-3 border-b border-dashed border-border/60 py-2 last:border-b-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{c.appName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {c.paused ? "Paused by VERBLIZR · " : ""}
                    Connected {formatRelative(c.connectedAt)}
                    {c.lastUsedAt ? ` · last used ${formatRelative(c.lastUsedAt)}` : ""}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-brand"
                  disabled={busy}
                  onClick={() => void onDisconnect(c.id)}
                >
                  {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Disconnect"}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AutomationCard({ app }: { app: AutomationApp }) {
  const { user, entitlements } = useAuth();
  const { config, optional } = usePublicConfig();
  const list = useAiConnections();
  const [howOpen, setHowOpen] = useState(false);
  const admin = isCompanyAdminRole(user?.role);
  const inCompany = Boolean(user?.company_id);
  const companyAllowed = entitlements[app.key] !== false;
  // B42: paused for every company by Super Admin; nothing to connect to.
  const platformOn = integrationLive(config, app.key);
  const allowed = platformOn && companyAllowed;
  const appUrl = safeHttps(optional(app.urlKey), "");
  const connected = (list.data ?? []).some((c) => c.kind === app.key);
  const companyName = (user as { company?: string | null } | null)?.company || user?.company_name || "your company";

  const status = !platformOn ? (
    <Badge variant="muted">Not available right now</Badge>
  ) : !allowed ? (
    <Badge variant="warning">Off for your company</Badge>
  ) : inCompany ? (
    <Badge variant="success">Allowed for {companyName}</Badge>
  ) : connected ? (
    <Badge variant="success">Connected</Badge>
  ) : (
    <Badge variant="muted">Not connected</Badge>
  );

  return (
    <IntegrationCard title={app.logo} status={status}>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {app.connectText}.{" "}
        {!platformOn
          ? `${app.name} is not available right now. Please try again later.`
          : allowed
          ? "Send meeting minutes, to-dos and notes to the apps your team already uses."
          : admin
            ? `Turn on the switch below to connect ${app.name}.`
            : app.askAdminText}
      </p>
      {allowed ? (
        <ul className="flex flex-wrap gap-2" aria-label={app.ideasLabel}>
          {app.ideas.map(([from, to]) => (
            <li key={from} className="rounded-lg border border-border/70 bg-muted/40 px-2.5 py-1.5 text-xs">
              {from} <span className="text-muted-foreground">→</span> {to}
            </li>
          ))}
        </ul>
      ) : null}
      {allowed ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {appUrl ? (
            <Button asChild className="gap-2">
              <a href={appUrl} target="_blank" rel="noreferrer">
                Connect {app.name}
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          ) : (
            <span className="text-sm text-muted-foreground">{app.name} link coming soon</span>
          )}
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            aria-expanded={howOpen}
            onClick={() => setHowOpen((v) => !v)}
          >
            How it works
          </button>
        </div>
      ) : null}
      {allowed && howOpen ? (
        <p className="text-sm text-muted-foreground">
          {app.howText}
        </p>
      ) : null}
      {admin && inCompany ? <AutomationCompanySwitch app={app} /> : null}
      {/* Pushes the connections list to the bottom, so side-by-side cards line up. */}
      <div className="flex-1" />
      <AutomationConnections app={app} />
    </IntegrationCard>
  );
}

function AsanaCard({ to }: { to: string }) {
  const status = useConnectorStatus();
  const asana = status.data?.asana;
  const chip = status.isLoading ? null : asana?.connected ? (
    asana.needsReconnect ? (
      <Badge variant="warning">Needs reconnecting</Badge>
    ) : (
      <Badge variant="success">Connected</Badge>
    )
  ) : (
    <Badge variant="muted">Not connected</Badge>
  );
  return (
    <IntegrationCard title={<CardName icon={ListTodo} name="Asana" />} status={chip}>
      <p className="flex-1 text-sm text-muted-foreground">
        Send meeting to-dos to Asana, each with the moment it was agreed.
      </p>
      <div>
        <Button asChild variant="outline" size="sm" className="gap-1">
          <Link to={to}>
            Open
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </IntegrationCard>
  );
}

function AiAssistantsCard({ to }: { to: string }) {
  const list = useAiConnections();
  // Only AI assistants (MCP); Zapier and Make have their own cards.
  const n = (list.data ?? []).filter((c) => !isAutomationKind(c.kind)).length;
  return (
    <IntegrationCard
      title={<CardName icon={Sparkles} name="AI assistants" />}
      status={list.isLoading ? null : <Badge variant={n > 0 ? "success" : "muted"}>{n > 0 ? `${n} connected` : "Not connected"}</Badge>}
    >
      <p className="flex-1 text-sm text-muted-foreground">
        Ask Claude and other AI apps about your meetings. Read-only, managed in Profile.
      </p>
      <div>
        <Button asChild variant="outline" size="sm" className="gap-1">
          <Link to={to}>
            Manage
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </IntegrationCard>
  );
}

export function IntegrationsPage() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const admin = isCompanyAdminRole(user?.role);
  const asanaPath = admin ? "/company/settings/asana" : "/app/integrations/asana";
  const accountPath = `${pathname.startsWith("/company") ? "/company" : "/app"}/account?tab=apps`;

  return (
    <div className={gridPageBleedClass("pb-6")}>
      <GridBlock>
        <GridBand>
          <h2 className="text-xl font-semibold tracking-tight">Integrations</h2>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Send your meetings, to-dos and notes to the tools you already use.
          </p>
        </GridBand>
        <GridCell>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AutomationCard app={ZAPIER} />
            <AutomationCard app={MAKE} />
            <AsanaCard to={asanaPath} />
            <AiAssistantsCard to={accountPath} />
          </div>
        </GridCell>
      </GridBlock>
    </div>
  );
}
